import { type FirebaseOptions, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';

/**
 * Firebase client bootstrap (TTP-243). No real Firebase project credentials
 * exist for this session (see .env.example) — every field below is
 * `string | undefined`, exactly as `firebase/app` accepts: an app initialized
 * with undefined fields still constructs successfully, it only fails calls
 * that actually hit the network (verifyIdToken-equivalent client calls),
 * which is fine for local dev against the Auth Emulator (below) or for
 * `pnpm --filter dashboard build`/type-check, neither of which talks to a
 * real project.
 */
// `demo-tt-trivio` matches the demo project id firebase.json's emulators
// already run under (same one `pnpm test:firestore-rules` at the repo root
// uses: `firebase emulators:exec --project demo-tt-trivio`) — used here only
// as a fallback `projectId`/`authDomain` when no real one is configured.
const DEMO_PROJECT_ID = 'demo-tt-trivio';

// `FirebaseOptions`' fields are typed as plain `string` (not `string |
// undefined`), so under `exactOptionalPropertyTypes` we need a fallback
// when a var is unset rather than passing `undefined` through. An empty
// `apiKey` specifically is NOT safe to fall back to, unlike the other
// fields: the Firebase JS SDK rejects a falsy `apiKey` client-side with
// `auth/invalid-api-key` before ever reaching the Auth Emulator (confirmed
// empirically in this session — signInWithEmailAndPassword/onAuthStateChanged
// threw that even with the emulator running and connectAuthEmulator called).
// A non-empty placeholder string is all the emulator needs (it doesn't
// validate the key's authenticity), so we fall back to one instead of ''.
const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? `${DEMO_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? DEMO_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
};

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);

/**
 * Whether to point the Auth client at the local emulator (port 9099,
 * already configured in firebase.json at the repo root — the same emulator
 * `pnpm test:firestore-rules` uses). Defaults to on in `vite dev`
 * (`import.meta.env.DEV`) so the onboarding flow is testable end-to-end
 * without a real Firebase project; `VITE_USE_FIREBASE_EMULATOR=false`
 * overrides that default (e.g. to point a local dev server at a real
 * staging project once one exists).
 */
function shouldUseAuthEmulator(): boolean {
  const flag = import.meta.env.VITE_USE_FIREBASE_EMULATOR;
  if (flag === 'false') {
    return false;
  }
  if (flag === 'true') {
    return true;
  }
  return import.meta.env.DEV;
}

let emulatorConnected = false;

/**
 * Idempotent — `connectAuthEmulator` throws if called twice on the same
 * `Auth` instance, which React 19 StrictMode's double-invoke of module-level
 * side effects (and Vite HMR re-evaluating this module) can trigger.
 */
export function ensureAuthEmulatorConnected(): void {
  if (emulatorConnected || !shouldUseAuthEmulator()) {
    return;
  }
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  emulatorConnected = true;
}

ensureAuthEmulatorConnected();
